/**
 * Renders dock-scoped no-fly zones (NFZs) on the map, plus an in-progress
 * draft polygon while the user is drawing a new one.
 *
 * Three logical pieces:
 *   - Finalized NFZs: red translucent fill + dashed red outline. Always-on
 *     visualization for every dock's `noFlyZones` (filtered to `visible !==
 *     false`). The same fill renders for all docks; we don't scope NFZ
 *     visibility per-dock because a user only ever interacts with one dock's
 *     missions at a time and obscuring NFZs from "other docks" would just be
 *     a footgun in multi-dock projects.
 *   - Vertex handles: small dots on the vertices of the NFZ currently in
 *     edit mode (`nfzEditingId`). The actual drag/delete behaviour for
 *     handles lives in DockStationsDemoPanel's edit UI (Phase 3+); this
 *     layer only renders the dots.
 *   - Draft polygon: a Polygon (if ≥3 verts) + LineString (≥2) + Points
 *     for the in-progress NFZ during draw mode. Lets the user see what
 *     they're building before finalizing.
 *
 * Follows the canonical dock-demo layer pattern:
 *   - All source/layer IDs prefixed `dock-demo-nfz-*` (no risk of colliding
 *     with platform Mapbox layers).
 *   - `setData` is NEVER gated on isStyleLoaded() — MapboxDraw can wedge
 *     that flag permanently false in dev. Only addSource/addLayer/moveLayer
 *     gate on it. See reference_mapbox_setdata_gating memory.
 *   - Self-healing via `style.load` + `styledata` listeners: any external
 *     code (style swap, MapboxDraw connect) that wipes our sources/layers
 *     triggers a full reinstall on the next styledata tick.
 */

import { useEffect, useRef } from 'react';
import type { Map as MapboxMap, MapLayerMouseEvent } from 'mapbox-gl';
import { useMapStore } from '../../stores';
import { useDockDemoStore } from '../../stores/dockDemoStore';
import type { DemoDockStation } from './types';

const SRC_NFZ = 'dock-demo-nfz-src';
const SRC_NFZ_VERTS = 'dock-demo-nfz-verts-src';
const SRC_NFZ_DRAFT = 'dock-demo-nfz-draft-src';

const LAYER_NFZ_FILL = 'dock-demo-nfz-fill-lyr';
const LAYER_NFZ_OUTLINE_HIT = 'dock-demo-nfz-outline-hit-lyr';
const LAYER_NFZ_OUTLINE = 'dock-demo-nfz-outline-lyr';
const LAYER_NFZ_VERTS = 'dock-demo-nfz-verts-lyr';
const LAYER_NFZ_DRAFT_FILL = 'dock-demo-nfz-draft-fill-lyr';
const LAYER_NFZ_DRAFT_LINE = 'dock-demo-nfz-draft-line-lyr';
const LAYER_NFZ_DRAFT_PTS = 'dock-demo-nfz-draft-pts-lyr';

const ALL_LAYERS = [
  LAYER_NFZ_FILL,
  LAYER_NFZ_OUTLINE_HIT,
  LAYER_NFZ_OUTLINE,
  LAYER_NFZ_VERTS,
  LAYER_NFZ_DRAFT_FILL,
  LAYER_NFZ_DRAFT_LINE,
  LAYER_NFZ_DRAFT_PTS,
] as const;

const ALL_SOURCES = [SRC_NFZ, SRC_NFZ_VERTS, SRC_NFZ_DRAFT] as const;

function isMissing(map: MapboxMap): boolean {
  for (const id of ALL_SOURCES) if (!map.getSource(id)) return true;
  for (const id of ALL_LAYERS) if (!map.getLayer(id)) return true;
  return false;
}

function buildNFZFeatures(stations: DemoDockStation[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature<GeoJSON.Polygon>[] = [];
  for (const dock of stations) {
    for (const nfz of dock.noFlyZones ?? []) {
      if (nfz.visible === false) continue;
      if (nfz.vertices.length < 3) continue;
      // Closed ring: first vertex repeated as last for GeoJSON Polygon.
      const ring: [number, number][] = [...nfz.vertices, nfz.vertices[0]];
      features.push({
        type: 'Feature',
        // Use `nfzId` (not `id`) so click-on-outline handlers can read the
        // same property name as vertex features — keeps the handler logic
        // uniform across source shapes.
        properties: { nfzId: nfz.id, dockId: dock.id, name: nfz.name ?? '' },
        geometry: { type: 'Polygon', coordinates: [ring] },
      });
    }
  }
  return { type: 'FeatureCollection', features };
}

function buildNFZVertexFeatures(
  stations: DemoDockStation[],
  editingId: string | null,
): GeoJSON.FeatureCollection {
  // Vertex handles are only shown for the NFZ currently in edit mode.
  // Drawing every NFZ's vertices all the time would clutter the map.
  if (!editingId) return { type: 'FeatureCollection', features: [] };
  const features: GeoJSON.Feature<GeoJSON.Point>[] = [];
  for (const dock of stations) {
    const nfz = (dock.noFlyZones ?? []).find((z) => z.id === editingId);
    if (!nfz) continue;
    for (let i = 0; i < nfz.vertices.length; i++) {
      features.push({
        type: 'Feature',
        properties: { nfzId: nfz.id, dockId: dock.id, index: i },
        geometry: { type: 'Point', coordinates: nfz.vertices[i] },
      });
    }
  }
  return { type: 'FeatureCollection', features };
}

function buildDraftFeatures(verts: [number, number][]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  // Closed polygon preview at ≥3 vertices — visually signals "this would
  // finalize as a valid NFZ".
  if (verts.length >= 3) {
    features.push({
      type: 'Feature',
      properties: { kind: 'polygon' },
      geometry: {
        type: 'Polygon',
        coordinates: [[...verts, verts[0]]],
      },
    });
  }
  // Open polyline at ≥2 vertices for the in-between state.
  if (verts.length >= 2) {
    features.push({
      type: 'Feature',
      properties: { kind: 'line' },
      geometry: { type: 'LineString', coordinates: verts },
    });
  }
  // Point dots for every vertex — visible feedback for the first click too.
  for (let i = 0; i < verts.length; i++) {
    features.push({
      type: 'Feature',
      properties: { kind: 'point', index: i },
      geometry: { type: 'Point', coordinates: verts[i] },
    });
  }
  return { type: 'FeatureCollection', features };
}

function ensureSourcesAndLayers(map: MapboxMap): void {
  if (!map.getSource(SRC_NFZ)) {
    map.addSource(SRC_NFZ, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }
  if (!map.getSource(SRC_NFZ_VERTS)) {
    map.addSource(SRC_NFZ_VERTS, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }
  if (!map.getSource(SRC_NFZ_DRAFT)) {
    map.addSource(SRC_NFZ_DRAFT, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }

  if (!map.getLayer(LAYER_NFZ_FILL)) {
    // Translucent red fill. 18% so the basemap and any overlapping route /
    // waypoint markers stay legible through the wash.
    map.addLayer({
      id: LAYER_NFZ_FILL,
      type: 'fill',
      source: SRC_NFZ,
      paint: {
        'fill-color': '#ef4444',
        'fill-opacity': 0.18,
      },
    });
  }
  if (!map.getLayer(LAYER_NFZ_OUTLINE_HIT)) {
    // Invisible wide hit-target on the same polygon source. The visible
    // outline above is 2px wide, which gives Mapbox a hairline hitbox
    // for clicks — users effectively can't hit it. This 18px transparent
    // line sits underneath the visible dashed outline and absorbs
    // mouseenter / click events within ±9 px of the edge, which is the
    // tolerance the click-to-insert affordance needs to feel usable.
    map.addLayer({
      id: LAYER_NFZ_OUTLINE_HIT,
      type: 'line',
      source: SRC_NFZ,
      paint: {
        'line-color': '#000000',
        'line-width': 18,
        'line-opacity': 0,
      },
    });
  }
  if (!map.getLayer(LAYER_NFZ_OUTLINE)) {
    // Dashed red outline. Mapbox auto-extracts polygon outlines for line
    // layers, so the same Polygon source feeds both fill and outline.
    map.addLayer({
      id: LAYER_NFZ_OUTLINE,
      type: 'line',
      source: SRC_NFZ,
      paint: {
        'line-color': '#ef4444',
        'line-width': 2,
        'line-dasharray': [2, 2],
        'line-opacity': 0.95,
      },
    });
  }
  if (!map.getLayer(LAYER_NFZ_VERTS)) {
    // Vertex handles for the NFZ in edit mode. Same sizing/halo as the
    // geofence vertex handles in DockHomeLayer (8 px circle, dark stroke)
    // for visual consistency across the docking-station drawing tools.
    map.addLayer({
      id: LAYER_NFZ_VERTS,
      type: 'circle',
      source: SRC_NFZ_VERTS,
      paint: {
        'circle-radius': 8,
        'circle-color': '#ef4444',
        'circle-stroke-width': 2,
        'circle-stroke-color': '#0f172a',
        'circle-opacity': 1,
      },
    });
  }

  // Draft layers — three filter-gated layers on a single source so we can
  // emit polygon + line + points in one FeatureCollection and have each
  // layer pick its slice. Slightly higher opacity than finalized NFZs so the
  // in-progress polygon reads as "active / being created".
  if (!map.getLayer(LAYER_NFZ_DRAFT_FILL)) {
    map.addLayer({
      id: LAYER_NFZ_DRAFT_FILL,
      type: 'fill',
      source: SRC_NFZ_DRAFT,
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: {
        'fill-color': '#ef4444',
        'fill-opacity': 0.22,
      },
    });
  }
  if (!map.getLayer(LAYER_NFZ_DRAFT_LINE)) {
    map.addLayer({
      id: LAYER_NFZ_DRAFT_LINE,
      type: 'line',
      source: SRC_NFZ_DRAFT,
      filter: ['==', ['geometry-type'], 'LineString'],
      paint: {
        'line-color': '#ef4444',
        'line-width': 2,
        'line-dasharray': [2, 2],
        'line-opacity': 1,
      },
    });
  }
  if (!map.getLayer(LAYER_NFZ_DRAFT_PTS)) {
    map.addLayer({
      id: LAYER_NFZ_DRAFT_PTS,
      type: 'circle',
      source: SRC_NFZ_DRAFT,
      filter: ['==', ['geometry-type'], 'Point'],
      paint: {
        'circle-radius': 6,
        'circle-color': '#ef4444',
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
        'circle-opacity': 1,
      },
    });
  }
}

function pinLayersToTop(map: MapboxMap): void {
  // Order matters: fill goes first (lowest), then outline (above fill),
  // then verts + draft (above outline). moveLayer with no `beforeId` moves
  // the layer to the very top, so calling in this order produces the right
  // final stack with NFZ_FILL underneath everything else NFZ-related.
  for (const id of ALL_LAYERS) {
    try {
      if (map.getLayer(id)) map.moveLayer(id);
    } catch {
      // ignore — layer may not exist yet
    }
  }
}

function teardownSourcesAndLayers(map: MapboxMap): void {
  for (const id of [...ALL_LAYERS].reverse()) {
    try {
      if (map.getLayer(id)) map.removeLayer(id);
    } catch {
      // map may be in teardown — defensive
    }
  }
  for (const id of ALL_SOURCES) {
    try {
      if (map.getSource(id)) map.removeSource(id);
    } catch {
      // ignore
    }
  }
}

export default function NoFlyZoneLayer() {
  const map = useMapStore((s) => s.map);
  const isMapReady = useMapStore((s) => s.isMapReady);
  const stations = useDockDemoStore((s) => s.dockStations);
  const nfzEditingId = useDockDemoStore((s) => s.nfzEditingId);
  const nfzDrawVertices = useDockDemoStore((s) => s.nfzDrawVertices);

  // Drag-state ref for the vertex-handle drag interaction (Phase 3). Held in
  // a ref so the window-level mousemove handler can read it without re-binding
  // on every render — same pattern as LegOverlayLayer's yaw-tip drag.
  const dragRef = useRef<null | { dockId: string; nfzId: string; index: number }>(null);

  // Install / teardown lifecycle. Deliberately excludes data-only deps so a
  // single vertex edit doesn't tear down + recreate every layer. Style
  // recovery via `style.load` (full reinstall) and `styledata` (reinstall
  // only if anything is missing).
  useEffect(() => {
    if (!map || !isMapReady) return;

    const reinstall = () => {
      ensureSourcesAndLayers(map);
      const s = useDockDemoStore.getState();
      const nfzSrc = map.getSource(SRC_NFZ);
      const vertSrc = map.getSource(SRC_NFZ_VERTS);
      const draftSrc = map.getSource(SRC_NFZ_DRAFT);
      if (nfzSrc && 'setData' in nfzSrc) nfzSrc.setData(buildNFZFeatures(s.dockStations));
      if (vertSrc && 'setData' in vertSrc) vertSrc.setData(buildNFZVertexFeatures(s.dockStations, s.nfzEditingId));
      if (draftSrc && 'setData' in draftSrc) draftSrc.setData(buildDraftFeatures(s.nfzDrawVertices));
      pinLayersToTop(map);
    };

    const repin = () => {
      if (isMissing(map)) {
        reinstall();
      } else {
        pinLayersToTop(map);
      }
    };

    if (map.isStyleLoaded()) reinstall();
    map.on('style.load', reinstall);
    map.on('styledata', repin);

    return () => {
      map.off('style.load', reinstall);
      map.off('styledata', repin);
      teardownSourcesAndLayers(map);
    };
  }, [map, isMapReady]);

  // Data update on every NFZ / edit / draft change. setData is safe even
  // when isStyleLoaded() is transiently false; only addSource/addLayer/
  // moveLayer need a loaded style. See reference_mapbox_setdata_gating.
  useEffect(() => {
    if (!map || !isMapReady) return;
    const styleLoaded = map.isStyleLoaded();
    if (styleLoaded) ensureSourcesAndLayers(map);

    const nfzSrc = map.getSource(SRC_NFZ);
    const vertSrc = map.getSource(SRC_NFZ_VERTS);
    const draftSrc = map.getSource(SRC_NFZ_DRAFT);
    if (nfzSrc && 'setData' in nfzSrc) {
      try { nfzSrc.setData(buildNFZFeatures(stations)); } catch { /* style mid-mutation */ }
    }
    if (vertSrc && 'setData' in vertSrc) {
      try { vertSrc.setData(buildNFZVertexFeatures(stations, nfzEditingId)); } catch { /* same */ }
    }
    if (draftSrc && 'setData' in draftSrc) {
      try { draftSrc.setData(buildDraftFeatures(nfzDrawVertices)); } catch { /* same */ }
    }
    if (styleLoaded) pinLayersToTop(map);
  }, [map, isMapReady, stations, nfzEditingId, nfzDrawVertices]);

  // Vertex-handle interactions (Phase 3). Active for the NFZ currently in
  // edit mode — handles are only rendered when nfzEditingId is set, so the
  // listeners simply never fire outside of edit mode.
  //  - mousedown on a vertex: start drag, disable map pan
  //  - window mousemove: live-update the vertex lng/lat via updateNoFlyZone
  //  - window mouseup: commit + re-enable map pan
  //  - contextmenu on a vertex: delete (refused if it would drop below 3)
  useEffect(() => {
    if (!map || !isMapReady) return;

    const onMouseEnter = () => {
      // Only override cursor when not already dragging — dragging cursor
      // is set in onMouseDown and we don't want this enter handler to
      // immediately replace 'grabbing' with 'move' on a sub-pixel hover wobble.
      if (!dragRef.current) map.getCanvas().style.cursor = 'move';
    };
    const onMouseLeave = () => {
      if (!dragRef.current) map.getCanvas().style.cursor = '';
    };

    const onMouseDown = (e: MapLayerMouseEvent) => {
      const feat = e.features?.[0];
      if (!feat || !feat.properties) return;
      const props = feat.properties as { dockId: string; nfzId: string; index: number };
      dragRef.current = {
        dockId: String(props.dockId),
        nfzId: String(props.nfzId),
        index: Number(props.index),
      };
      // Disable map pan so the vertex drag doesn't accidentally pan the map.
      // Re-enabled in window mouseup.
      map.dragPan.disable();
      map.getCanvas().style.cursor = 'grabbing';
      e.preventDefault();
    };

    const onWindowMouseMove = (ev: MouseEvent) => {
      const ds = dragRef.current;
      if (!ds) return;
      const canvas = map.getCanvas();
      const rect = canvas.getBoundingClientRect();
      const ll = map.unproject([ev.clientX - rect.left, ev.clientY - rect.top]);
      const state = useDockDemoStore.getState();
      const dock = state.dockStations.find((d) => d.id === ds.dockId);
      const nfz = dock?.noFlyZones?.find((z) => z.id === ds.nfzId);
      if (!nfz) return;
      const verts: [number, number][] = nfz.vertices.map((v, i) =>
        i === ds.index ? [ll.lng, ll.lat] : v,
      );
      state.updateNoFlyZone(ds.dockId, ds.nfzId, { vertices: verts });
    };

    const onWindowMouseUp = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      map.dragPan.enable();
      map.getCanvas().style.cursor = '';
    };

    const onContextMenu = (e: MapLayerMouseEvent) => {
      e.preventDefault();
      const feat = e.features?.[0];
      if (!feat || !feat.properties) return;
      const props = feat.properties as { dockId: string; nfzId: string; index: number };
      const dockId = String(props.dockId);
      const nfzId = String(props.nfzId);
      const index = Number(props.index);
      const state = useDockDemoStore.getState();
      const dock = state.dockStations.find((d) => d.id === dockId);
      const nfz = dock?.noFlyZones?.find((z) => z.id === nfzId);
      if (!nfz) return;
      // Minimum 3 vertices — a 2-vertex polygon is a line, not a zone.
      if (nfz.vertices.length <= 3) return;
      const verts = nfz.vertices.filter((_, i) => i !== index);
      state.updateNoFlyZone(dockId, nfzId, { vertices: verts });
    };

    // Click on the polygon outline while editing → insert a new vertex at
    // that point, placed between the two endpoints of the clicked edge.
    // Scoped to the editing NFZ: clicks on OTHER NFZs' outlines are ignored
    // (no accidental edits to neighbouring zones). Distance comparison in
    // raw degree-space is fine because the click is by construction near
    // the visible outline, so the min-distance edge is the visually-correct
    // one regardless of lat/lng scale skew.
    const pointToSegment2D = (
      p: [number, number],
      a: [number, number],
      b: [number, number],
    ): number => {
      const dx = b[0] - a[0];
      const dy = b[1] - a[1];
      const lenSq = dx * dx + dy * dy;
      if (lenSq === 0) return Math.hypot(p[0] - a[0], p[1] - a[1]);
      let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lenSq;
      t = Math.max(0, Math.min(1, t));
      return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
    };

    const onOutlineClick = (e: MapLayerMouseEvent) => {
      const feat = e.features?.[0];
      if (!feat || !feat.properties) return;
      const props = feat.properties as { nfzId: string; dockId: string };
      const state = useDockDemoStore.getState();
      // Only allow inserting on the NFZ currently being edited. Without
      // this gate every NFZ outline becomes click-to-insert, which makes
      // navigating around the map noisy and footgun-y.
      if (state.nfzEditingId !== props.nfzId) return;
      const dock = state.dockStations.find((d) => d.id === props.dockId);
      const nfz = dock?.noFlyZones?.find((z) => z.id === props.nfzId);
      if (!nfz) return;
      const click: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      const n = nfz.vertices.length;
      let bestEdge = 0;
      let bestDist = Infinity;
      for (let i = 0; i < n; i++) {
        const a = nfz.vertices[i];
        const b = nfz.vertices[(i + 1) % n];
        const d = pointToSegment2D(click, a, b);
        if (d < bestDist) {
          bestDist = d;
          bestEdge = i;
        }
      }
      // Insert AFTER bestEdge so the new vertex sits between the two
      // endpoints of the clicked edge.
      const verts: [number, number][] = [
        ...nfz.vertices.slice(0, bestEdge + 1),
        click,
        ...nfz.vertices.slice(bestEdge + 1),
      ];
      state.updateNoFlyZone(props.dockId, props.nfzId, { vertices: verts });
    };

    const onOutlineMouseEnter = () => {
      // 'copy' cursor signals "click to add" — distinct from 'move' (drag
      // existing vertex) and 'grabbing' (active drag). Only fires when the
      // user is in edit mode for the hovered NFZ; otherwise leave the
      // cursor alone so panning still feels normal.
      const state = useDockDemoStore.getState();
      if (state.nfzEditingId && !dragRef.current) {
        map.getCanvas().style.cursor = 'copy';
      }
    };
    const onOutlineMouseLeave = () => {
      if (!dragRef.current) map.getCanvas().style.cursor = '';
    };

    map.on('mouseenter', LAYER_NFZ_VERTS, onMouseEnter);
    map.on('mouseleave', LAYER_NFZ_VERTS, onMouseLeave);
    map.on('mousedown', LAYER_NFZ_VERTS, onMouseDown);
    map.on('contextmenu', LAYER_NFZ_VERTS, onContextMenu);
    map.on('click', LAYER_NFZ_OUTLINE_HIT, onOutlineClick);
    map.on('mouseenter', LAYER_NFZ_OUTLINE_HIT, onOutlineMouseEnter);
    map.on('mouseleave', LAYER_NFZ_OUTLINE_HIT, onOutlineMouseLeave);
    window.addEventListener('mousemove', onWindowMouseMove);
    window.addEventListener('mouseup', onWindowMouseUp);

    return () => {
      map.off('mouseenter', LAYER_NFZ_VERTS, onMouseEnter);
      map.off('mouseleave', LAYER_NFZ_VERTS, onMouseLeave);
      map.off('mousedown', LAYER_NFZ_VERTS, onMouseDown);
      map.off('contextmenu', LAYER_NFZ_VERTS, onContextMenu);
      map.off('click', LAYER_NFZ_OUTLINE_HIT, onOutlineClick);
      map.off('mouseenter', LAYER_NFZ_OUTLINE_HIT, onOutlineMouseEnter);
      map.off('mouseleave', LAYER_NFZ_OUTLINE_HIT, onOutlineMouseLeave);
      window.removeEventListener('mousemove', onWindowMouseMove);
      window.removeEventListener('mouseup', onWindowMouseUp);
      // Safety: if the layer unmounts mid-drag, re-enable pan and reset cursor.
      if (dragRef.current) {
        map.dragPan.enable();
        map.getCanvas().style.cursor = '';
        dragRef.current = null;
      }
    };
  }, [map, isMapReady]);

  return null;
}
